import Dashboard          from "views/Dashboard.js";
import Icons              from "views/Icons.js";
import Notifications      from "views/Notifications.js";
import TableList          from "views/TableList.js";
import UserProfile        from "views/UserProfile.js";
import AddProducts        from "views/AddProducts.js";
import CatalogProductsList  from "views/CatalogProductsList";
import CatalogProductStudio from "views/CatalogProductStudio";
import Collections        from "views/Collections.js";
import CMSPanel           from "views/CmsPanels.js";
import ProductsList       from "views/ProductsList";
import SalesPage          from "views/Sales";
import RecentSalesPage    from "views/RecentSales";
import RecentReturnsPage  from "views/RecentReturns";
import RecentExchangesPage from "views/RecentExchanges";
import AdministrationPanel from "views/AdministrationPanel";
import LogIn              from "views/LogIn";
import Transactions       from "views/Transactions";
import CompanyProfile     from "views/CompanyProfile";
import ReturnsPage        from "views/returnPage";
import ExchangesPage      from "views/Exchanges";
import Orders             from "views/Orders";
import CreateOrder        from "views/CreateOrder";
import Customers          from "views/Customers";
import CustomerDetail     from "views/CustomerDetail";
import StockCheckSessions from "views/StockCheckSessions";
import StockCheckStudio   from "views/StockCheckStudio";
import MetaAdsProductSets from "views/marketing/MetaAdsProductSets";
import MetaProductSetManage from "views/marketing/MetaProductSetManage";
import PrintCenter        from "views/printCenter/PrintCenter";
import { PRINT_CENTER_VIEW_ROLES } from "views/printCenter/printCenterModel";
import Analytics          from "views/analytics/Analytics";

// ── Role sets ─────────────────────────────────────────────────────────────────
// Routes with no `roles` field are visible to all authenticated users.
// These arrays are referenced directly so identity checks work for filtering.
const MANAGER_PLUS = ["manager", "admin", "owner"];
const ADMIN_OWNER  = ["admin", "owner"];

// ── Child routes ──────────────────────────────────────────────────────────────
// Kept in the flat routes array so React Router registers them.
// `sidebar: false` prevents them from appearing as top-level sidebar items.
// They are also referenced in each parent's `subItems` for accordion display.

const recentSalesRoute = {
  path: "/sales/history",
  name: "Recent Sales",
  rtlName: "المبيعات الأخيرة",
  icon: "tim-icons icon-calendar-60",
  component: <RecentSalesPage />,
  layout: "/admin",
  authentication: true,
  sidebar: false,
};

const recentReturnsRoute = {
  path: "/returns/history",
  name: "Recent Returns",
  rtlName: "المرتجعات الأخيرة",
  icon: "tim-icons icon-paper",
  component: <RecentReturnsPage />,
  layout: "/admin",
  authentication: true,
  sidebar: false,
};

const recentExchangesRoute = {
  path: "/exchanges/history",
  name: "Recent Exchanges",
  rtlName: "التبادلات الأخيرة",
  icon: "tim-icons icon-bullet-list-67",
  component: <RecentExchangesPage />,
  layout: "/admin",
  authentication: true,
  sidebar: false,
};

const collectionsRoute = {
  path: "/catalog/collections",
  name: "Collections",
  rtlName: "المجموعات",
  icon: "tim-icons icon-tag",
  component: <Collections />,
  layout: "/admin",
  authentication: true,
  sidebar: false,
};

// ── Deep-link only routes (never in sidebar) ──────────────────────────────────

// A product set inside Marketing → Meta Ads. `activeParent` keeps the
// "Meta Ads" sidebar item highlighted while it is open.
const metaProductSetRoute = {
  path: "/marketing/meta-ads/product-sets/:setId",
  name: "Meta Ads",
  rtlName: "إعلانات ميتا",
  icon: "tim-icons icon-sound-wave",
  component: <MetaProductSetManage />,
  layout: "/admin",
  authentication: true,
  roles: MANAGER_PLUS,
  sidebar: false,
  activeParent: "/marketing/meta-ads",
};

const stockCheckStudioRoute = {
  path: "/stock-check/sessions/:sessionId",
  name: "Stock Check Studio",
  rtlName: "فحص المخزون",
  icon: "tim-icons icon-zoom-split",
  component: <StockCheckStudio />,
  layout: "/admin",
  authentication: true,
  sidebar: false,
};

const productStudioNewRoute = {
  path: "/catalog/products/new",
  name: "Product Studio",
  rtlName: "لوحة المنتجات",
  icon: "tim-icons icon-atom",
  component: <CatalogProductStudio />,
  layout: "/admin",
  authentication: true,
  sidebar: false,
};

const productStudioEditRoute = {
  path: "/catalog/products/:productId",
  name: "Product Studio",
  rtlName: "لوحة المنتجات",
  icon: "tim-icons icon-atom",
  component: <CatalogProductStudio />,
  layout: "/admin",
  authentication: true,
  sidebar: false,
};

const createOrderRoute = {
  path: "/orders/create",
  name: "Create Order",
  rtlName: "إنشاء طلب",
  icon: "tim-icons icon-simple-add",
  component: <CreateOrder />,
  layout: "/admin",
  authentication: true,
  sidebar: false,
};

const customerDetailRoute = {
  path: "/customers/:customerId",
  name: "Customer Detail",
  rtlName: "تفاصيل العميل",
  icon: "tim-icons icon-single-02",
  component: <CustomerDetail />,
  layout: "/admin",
  authentication: true,
  sidebar: false,
};

// Analytics sections share one page shell; each section is its own URL so it can
// be bookmarked, but only the overview appears in the sidebar.
const analyticsSectionRoute = (section, roles = MANAGER_PLUS) => ({
  path: `/analytics/${section}`,
  name: "Analytics",
  rtlName: "التحليلات",
  icon: "tim-icons icon-chart-bar-32",
  component: <Analytics section={section} />,
  layout: "/admin",
  authentication: true,
  roles,
  sidebar: false,
  activeParent: "/analytics",
});

const analyticsSectionRoutes = [
  analyticsSectionRoute("traffic"),
  analyticsSectionRoute("meta-ads"),
  analyticsSectionRoute("sales"),
  analyticsSectionRoute("products"),
  analyticsSectionRoute("inventory"),
  analyticsSectionRoute("profitability", ADMIN_OWNER),
  analyticsSectionRoute("insights"),
  analyticsSectionRoute("reports"),
];

// ── Route definitions ──────────────────────────────────────────────────────────
//
// Section header entries (`sectionLabel: true`) are rendered as visual dividers.
// They have no path/layout and are invisible to React Router's getRoutes().
//
// Role visibility:
//   no `roles` field   → all authenticated users
//   MANAGER_PLUS       → manager, admin, owner
//   ADMIN_OWNER        → admin, owner only
//
// Sidebar order: store ops (daily use) → inventory → finance → config → account → legacy

var routes = [

  // ════════════════════════════════════════════════════════════════════════════
  //  STORE OPERATIONS  (all authenticated users)
  // ════════════════════════════════════════════════════════════════════════════
  {
    sectionLabel: true,
    name: "Store Operations",
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    rtlName: "لوحة القيادة",
    icon: "tim-icons icon-chart-pie-36",
    component: <Dashboard />,
    layout: "/admin",
    authentication: true,
  },
  {
    path: "/sales",
    name: "Sales",
    rtlName: "لوحة القيادة",
    icon: "tim-icons icon-cart",
    component: <SalesPage />,
    layout: "/admin",
    authentication: true,
    subItems: [recentSalesRoute],
  },
  recentSalesRoute,
  {
    path: "/returns",
    name: "Returns",
    rtlName: "لوحة القيادة",
    icon: "tim-icons icon-refresh-01",
    component: <ReturnsPage />,
    layout: "/admin",
    authentication: true,
    subItems: [recentReturnsRoute],
  },
  recentReturnsRoute,
  {
    path: "/exchanges",
    name: "Exchanges",
    rtlName: "لوحة القيادة",
    icon: "tim-icons icon-link-72",
    component: <ExchangesPage />,
    layout: "/admin",
    authentication: true,
    subItems: [recentExchangesRoute],
  },
  recentExchangesRoute,
  {
    path: "/orders",
    name: "Orders",
    rtlName: "الطلبات",
    icon: "tim-icons icon-delivery-fast",
    component: <Orders />,
    layout: "/admin",
    authentication: true,
  },
  createOrderRoute,
  {
    // Same visibility as Orders: customers are managed around orders.
    path: "/customers",
    name: "Customers",
    rtlName: "العملاء",
    icon: "tim-icons icon-single-02",
    component: <Customers />,
    layout: "/admin",
    authentication: true,
  },
  customerDetailRoute,

  // ════════════════════════════════════════════════════════════════════════════
  //  ANALYTICS  (manager+; cost and profit are admin/owner, enforced by the API)
  // ════════════════════════════════════════════════════════════════════════════
  {
    sectionLabel: true,
    name: "Analytics",
    labelKey: "navigation.analytics",
    roles: MANAGER_PLUS,
  },
  {
    path: "/analytics",
    name: "Analytics",
    rtlName: "التحليلات",
    icon: "tim-icons icon-chart-bar-32",
    component: <Analytics section="overview" />,
    layout: "/admin",
    authentication: true,
    roles: MANAGER_PLUS,
  },
  ...analyticsSectionRoutes,

  // ════════════════════════════════════════════════════════════════════════════
  //  INVENTORY  (manager+)
  // ════════════════════════════════════════════════════════════════════════════
  {
    sectionLabel: true,
    name: "Inventory",
    roles: MANAGER_PLUS,
  },
  {
    path: "/catalog/products",
    name: "Catalog",
    rtlName: "لوحة المنتجات",
    icon: "tim-icons icon-bag-16",
    component: <CatalogProductsList />,
    layout: "/admin",
    authentication: true,
    roles: MANAGER_PLUS,
    subItems: [collectionsRoute],
  },
  collectionsRoute,
  productStudioNewRoute,
  productStudioEditRoute,
  {
    path: "/stock-check",
    name: "Stock Check",
    rtlName: "فحص المخزون",
    icon: "tim-icons icon-zoom-split",
    component: <StockCheckSessions />,
    layout: "/admin",
    authentication: true,
    roles: MANAGER_PLUS,
  },
  stockCheckStudioRoute,

  // ════════════════════════════════════════════════════════════════════════════
  //  PRINTING  (manager+) — the Print Center owns label printing. It reads
  //  products and stock; Products keeps managing them.
  // ════════════════════════════════════════════════════════════════════════════
  {
    sectionLabel: true,
    name: "Printing",
    labelKey: "navigation.printing",
    roles: MANAGER_PLUS,
  },
  {
    path: "/print-center",
    name: "Print Center",
    rtlName: "مركز الطباعة",
    icon: "fa-solid fa-print",
    component: <PrintCenter />,
    layout: "/admin",
    authentication: true,
    roles: PRINT_CENTER_VIEW_ROLES,
  },

  // ════════════════════════════════════════════════════════════════════════════
  //  MARKETING  (manager+) — advertising channels. Meta Ads today; Google Ads,
  //  campaigns or email can join this section as sibling routes later.
  // ════════════════════════════════════════════════════════════════════════════
  {
    sectionLabel: true,
    name: "Marketing",
    labelKey: "navigation.marketing",
    roles: MANAGER_PLUS,
  },
  {
    path: "/marketing/meta-ads",
    name: "Meta Ads",
    rtlName: "إعلانات ميتا",
    icon: "tim-icons icon-sound-wave",
    component: <MetaAdsProductSets />,
    layout: "/admin",
    authentication: true,
    roles: MANAGER_PLUS,
  },
  metaProductSetRoute,

  // ════════════════════════════════════════════════════════════════════════════
  //  FINANCE  (manager+)
  // ════════════════════════════════════════════════════════════════════════════
  {
    sectionLabel: true,
    name: "Finance",
    roles: MANAGER_PLUS,
  },
  {
    path: "/transactions",
    name: "Transactions",
    rtlName: "قائمة الجدول",
    icon: "tim-icons icon-money-coins",
    component: <Transactions />,
    layout: "/admin",
    authentication: true,
    roles: MANAGER_PLUS,
  },

  // ════════════════════════════════════════════════════════════════════════════
  //  CONFIG & ADMIN  (manager+ for operations; admin/owner for company config)
  // ════════════════════════════════════════════════════════════════════════════
  {
    sectionLabel: true,
    name: "Config & Admin",
    roles: MANAGER_PLUS,
  },
  {
    path: "/cms",
    name: "Reference Data",
    rtlName: "بيانات مرجعية",
    icon: "tim-icons icon-components",
    component: <CMSPanel />,
    layout: "/admin",
    authentication: true,
    roles: MANAGER_PLUS,
  },
  {
    path: "/settings",
    name: "Administration",
    rtlName: "لوحة القيادة",
    icon: "tim-icons icon-settings-gear-63",
    component: <AdministrationPanel />,
    layout: "/admin",
    authentication: true,
    roles: MANAGER_PLUS,
  },
  {
    path: "/company-profile",
    name: "Company Profile",
    rtlName: "ملف تعريفي للمستخدم",
    icon: "tim-icons icon-bank",
    component: <CompanyProfile />,
    layout: "/admin",
    authentication: false,
    roles: ADMIN_OWNER,
  },

  // ════════════════════════════════════════════════════════════════════════════
  //  ACCOUNT  (all authenticated users)
  // ════════════════════════════════════════════════════════════════════════════
  {
    sectionLabel: true,
    name: "Account",
  },
  {
    path: "/user-profile",
    name: "User Profile",
    rtlName: "ملف تعريفي للمستخدم",
    icon: "tim-icons icon-single-02",
    component: <UserProfile />,
    authentication: false,
    layout: "/admin",
  },

  // ════════════════════════════════════════════════════════════════════════════
  //  LEGACY  (admin/owner only — older workflows kept for compatibility)
  // ════════════════════════════════════════════════════════════════════════════
  {
    sectionLabel: true,
    name: "Legacy",
    roles: ADMIN_OWNER,
  },
  {
    path: "/add/products",
    name: "Legacy Intake",
    rtlName: "لوحة القيادة",
    icon: "tim-icons icon-cloud-upload-94",
    component: <AddProducts />,
    layout: "/admin",
    authentication: true,
    roles: ADMIN_OWNER,
  },
  {
    path: "/products",
    name: "Legacy Products",
    rtlName: "لوحة القيادة",
    icon: "tim-icons icon-basket-simple",
    component: <ProductsList />,
    layout: "/admin",
    authentication: true,
    roles: ADMIN_OWNER,
  },

  // ════════════════════════════════════════════════════════════════════════════
  //  HIDDEN — template/demo pages; route kept for direct URL access only
  // ════════════════════════════════════════════════════════════════════════════
  {
    path: "/icons",
    name: "Icons",
    rtlName: "الرموز",
    icon: "tim-icons icon-palette",
    component: <Icons />,
    layout: "/admin",
    authentication: true,
    sidebar: false,
  },
  {
    path: "/notifications",
    name: "Notifications",
    rtlName: "إخطارات",
    icon: "tim-icons icon-bell-55",
    component: <Notifications />,
    layout: "/admin",
    authentication: true,
    sidebar: false,
  },
  {
    path: "/tables",
    name: "Table List",
    rtlName: "قائمة الجدول",
    icon: "tim-icons icon-puzzle-10",
    component: <TableList />,
    layout: "/admin",
    sidebar: false,
  },

  // ════════════════════════════════════════════════════════════════════════════
  //  AUTH
  // ════════════════════════════════════════════════════════════════════════════
  {
    path: "/login",
    name: "Log In",
    rtlName: "ملف تعريفي للمستخدم",
    icon: "tim-icons icon-lock-circle",
    component: <LogIn />,
    redirect: true,
    layout: "/admin",
    authentication: false,
  },
];

export default routes;
